import { ButtonToggle } from '@agilox/ui-common';
import { NgTemplateOutlet } from '@angular/common';
import {
	Component,
	contentChild,
	effect,
	input,
	model,
	output,
	signal,
	untracked,
} from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';
import { IconModule } from '../icon/icon.module';
import { TooltipModule } from '../tooltip/tooltip.module';
import { ButtonToggleContentDirective } from './directives/button-toggle-content.directive';

@Component({
	selector: 'ui-button-toggle',
	imports: [TooltipModule, IconModule, NgTemplateOutlet],
	templateUrl: 'button-toggle.component.html',
})
export class ButtonToggleComponent implements FormValueControl<any> {
	multiple = input<boolean>(false);
	tabindex = input<number>(0);
	disabled = input<boolean>(false);
	invalid = input<boolean>(false);

	buttons = model<ButtonToggle[]>([]);
	value = model<any>(undefined);

	valueChanged = output<any>();
	touch = output<void>();

	private _selectedValues = signal<Set<string | number | boolean>>(new Set());
	private _value = signal<string | number | boolean | undefined>(undefined);

	constructor() {
		effect(() => {
			const value = this.value();
			untracked(() => this.showValue(value));
		});
	}

	/** The field writing in; never written back, so the buttons cannot echo. */
	private showValue(value: any): void {
		if (this.multiple() && Array.isArray(value)) {
			this._selectedValues.set(new Set(value));
		}
		this._value.set(value);
	}

	private _focusedIndex = signal<number>(-1);

	buttonToggleContentDirective = contentChild(ButtonToggleContentDirective);

	onClick(button: ButtonToggle) {
		if (this.disabled() || button.disabled) {
			return;
		}

		let next: any;
		if (this.multiple() && typeof button.value !== 'boolean') {
			if (this._selectedValues().has(button.value)) {
				this._selectedValues().delete(button.value);
			} else {
				this._selectedValues().add(button.value);
			}
			next = Array.from(this._selectedValues());
		} else {
			this._value.set(button.value);
			next = button.value;
		}

		this.value.set(next);
		this.valueChanged.emit(next);
		this.touch.emit();
	}

	onKeyDown(event: KeyboardEvent, currentIndex: number): void {
		let newIndex = currentIndex;
		switch (event.key) {
			case 'ArrowLeft':
			case 'ArrowUp':
				event.preventDefault();
				newIndex = this.getPreviousEnabledIndex(currentIndex);
				break;
			case 'ArrowRight':
			case 'ArrowDown':
				event.preventDefault();
				newIndex = this.getNextEnabledIndex(currentIndex);
				break;
			default:
				return;
		}

		if (newIndex !== -1 && newIndex !== currentIndex) {
			this._focusedIndex.set(newIndex);
			this.focusButton(newIndex);
		}
	}

	private getPreviousEnabledIndex(currentIndex: number): number {
		const buttons = this.buttons();
		for (let i = currentIndex - 1; i >= 0; i--) {
			if (!buttons[i].disabled) {
				return i;
			}
		}
		for (let i = buttons.length - 1; i > currentIndex; i--) {
			if (!buttons[i].disabled) {
				return i;
			}
		}
		return currentIndex;
	}

	private getNextEnabledIndex(currentIndex: number): number {
		const buttons = this.buttons();
		for (let i = currentIndex + 1; i < buttons.length; i++) {
			if (!buttons[i].disabled) {
				return i;
			}
		}
		for (let i = 0; i < currentIndex; i++) {
			if (!buttons[i].disabled) {
				return i;
			}
		}
		return currentIndex;
	}

	private focusButton(index: number): void {
		setTimeout(() => {
			const buttonElement = document.querySelector(
				`[data-cy="ui-button-toggle-${index}"]`
			) as HTMLButtonElement;
			if (buttonElement) {
				buttonElement.focus();
			}
		}, 0);
	}

	getButtonTabIndex(index: number): number {
		const buttons = this.buttons();

		if (buttons[index].disabled || this.disabled()) {
			return -1;
		}

		return this.tabindex();
	}

	onFocus(index: number): void {
		this._focusedIndex.set(index);
	}

	/**
	 * Sets the button's selected style if the value is matching
	 * @param value
	 */
	isValueSelected(value: string | number | boolean): boolean {
		if (this.multiple()) {
			return this._selectedValues().has(value);
		}
		return this._value() === value;
	}
}
